import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Minus, Plus } from "lucide-react";

const REGIONS = ["Toute la Bretagne", "Finistère", "Côtes-d'Armor", "Morbihan", "Ille-et-Vilaine"];

export default function SearchBar({ compact = false, defaultValues = {} }) {
  const navigate = useNavigate();
  const [region, setRegion] = useState(defaultValues.region || "Toute la Bretagne");
  const [city, setCity] = useState(defaultValues.city || "");
  const [checkIn, setCheckIn] = useState(defaultValues.check_in || "");
  const [checkOut, setCheckOut] = useState(defaultValues.check_out || "");
  const [adults, setAdults] = useState(Number(defaultValues.adults || 2));
  const [children, setChildren] = useState(Number(defaultValues.children || 0));
  const [infants, setInfants] = useState(Number(defaultValues.infants || 0));
  const [pets, setPets] = useState(Number(defaultValues.pets || 0));
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  const guests = adults + children;
  const summary = (() => {
    const parts = [`${guests} voyageur${guests > 1 ? "s" : ""}`];
    if (infants > 0) parts.push(`${infants} bébé${infants > 1 ? "s" : ""}`);
    if (pets > 0) parts.push(`${pets} animal${pets > 1 ? "ux" : ""}`);
    return parts.join(" · ");
  })();

  useEffect(() => {
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submit = (e) => {
    e?.preventDefault?.();
    const params = new URLSearchParams();
    if (region && region !== "Toute la Bretagne") params.set("region", region);
    if (city) params.set("city", city);
    if (checkIn) params.set("check_in", checkIn);
    if (checkOut) params.set("check_out", checkOut);
    if (guests) params.set("guests", String(guests));
    if (adults) params.set("adults", String(adults));
    if (children) params.set("children", String(children));
    if (infants) params.set("infants", String(infants));
    if (pets > 0) params.set("pets", "1");
    navigate(`/search?${params.toString()}`);
  };

  const Stepper = ({ label, sub, val, set, min, testid }) => (
    <div className="flex items-center justify-between py-2.5">
      <div>
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-[11px] text-neutral-500">{sub}</div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid={`sb-${testid}-dec`}
          onClick={() => set(Math.max(min, val - 1))}
          disabled={val <= min}
          className="w-7 h-7 border border-black/20 flex items-center justify-center disabled:opacity-30 rounded-full"
        >
          <Minus className="w-3 h-3" />
        </button>
        <span data-testid={`sb-${testid}-val`} className="w-5 text-center text-sm font-semibold">{val}</span>
        <button
          type="button"
          data-testid={`sb-${testid}-inc`}
          onClick={() => set(val + 1)}
          className="w-7 h-7 border border-black/20 flex items-center justify-center rounded-full"
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
    </div>
  );

  return (
    <form
      onSubmit={submit}
      data-testid="search-bar"
      className={`bg-white shadow-2xl border border-black/5 ${compact ? "rounded-full p-1.5" : "rounded-2xl sm:rounded-full p-2 sm:p-2"} w-full`}
    >
      {/* MOBILE LAYOUT — grid 2 col compact, button full width below */}
      <div className="grid grid-cols-2 gap-1.5 sm:hidden">
        <label className="px-3 py-2 hover:bg-neutral-50 transition-colors rounded-xl cursor-text bg-neutral-50/50">
          <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 font-bold">Région</div>
          <select
            data-testid="search-region-m"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="w-full bg-transparent text-[13px] font-semibold outline-none cursor-pointer mt-0.5 truncate"
          >
            {REGIONS.map((r) => <option key={r}>{r}</option>)}
          </select>
        </label>
        <label className="px-3 py-2 hover:bg-neutral-50 transition-colors rounded-xl cursor-text bg-neutral-50/50">
          <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 font-bold">Ville</div>
          <input
            data-testid="search-city-m"
            type="text"
            placeholder="Saint-Malo…"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="w-full bg-transparent text-[13px] font-semibold outline-none placeholder:text-neutral-400 mt-0.5"
          />
        </label>
        <label className="px-3 py-2 hover:bg-neutral-50 transition-colors rounded-xl cursor-text bg-neutral-50/50">
          <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 font-bold">Arrivée</div>
          <input
            data-testid="search-checkin-m"
            type="date"
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
            className="w-full bg-transparent text-[13px] font-semibold outline-none mt-0.5"
          />
        </label>
        <label className="px-3 py-2 hover:bg-neutral-50 transition-colors rounded-xl cursor-text bg-neutral-50/50">
          <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 font-bold">Départ</div>
          <input
            data-testid="search-checkout-m"
            type="date"
            value={checkOut}
            onChange={(e) => setCheckOut(e.target.value)}
            className="w-full bg-transparent text-[13px] font-semibold outline-none mt-0.5"
          />
        </label>
        <div className="relative col-span-2" ref={popRef}>
          <button
            type="button"
            data-testid="search-guests-trigger-m"
            onClick={() => setOpen((o) => !o)}
            className="w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors rounded-xl bg-neutral-50/50"
          >
            <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 font-bold">Voyageurs</div>
            <div className="text-[13px] font-semibold mt-0.5 truncate">{summary}</div>
          </button>
          {open && (
            <div
              data-testid="search-guests-pop-m"
              className="absolute left-0 right-0 mt-2 bg-white border border-black/15 shadow-2xl rounded-lg p-4 z-50"
            >
              <div className="divide-y divide-black/5">
                <Stepper label="Adultes" sub="13 ans et +" val={adults} set={setAdults} min={1} testid="adults" />
                <Stepper label="Enfants" sub="2 – 12 ans" val={children} set={setChildren} min={0} testid="children" />
                <Stepper label="Bébés" sub="moins de 2 ans" val={infants} set={setInfants} min={0} testid="infants" />
                <Stepper label="Animaux" sub="logements pet-friendly" val={pets} set={setPets} min={0} testid="pets" />
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="mt-3 w-full text-xs uppercase tracking-[0.18em] font-bold underline"
              >
                Valider
              </button>
            </div>
          )}
        </div>
        <button
          data-testid="search-submit-btn-m"
          type="submit"
          className="col-span-2 mt-1 bg-[#0A0A0A] hover:bg-[#1B2A49] text-white py-3 rounded-xl flex items-center justify-center gap-2 transition-colors font-semibold text-sm"
        >
          <Search className="w-4 h-4" /> Chercher
        </button>
      </div>

      {/* DESKTOP LAYOUT — original horizontal pill */}
      <div className="hidden sm:flex sm:flex-row sm:items-center w-full">
        <label className="flex-1 px-5 py-3 hover:bg-neutral-50 transition-colors rounded-full cursor-text">
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold">Région</div>
          <select
            data-testid="search-region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="w-full bg-transparent text-sm font-semibold outline-none cursor-pointer mt-0.5"
          >
            {REGIONS.map((r) => <option key={r}>{r}</option>)}
          </select>
        </label>
        <div className="w-px h-8 bg-black/10 self-center" />
        <label className="flex-1 px-5 py-3 hover:bg-neutral-50 transition-colors rounded-full cursor-text">
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold">Ville</div>
          <input
            data-testid="search-city"
            type="text"
            placeholder="Saint-Malo, Vannes..."
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="w-full bg-transparent text-sm font-semibold outline-none placeholder:text-neutral-400 mt-0.5"
          />
        </label>
        <div className="w-px h-8 bg-black/10 self-center" />
        <label className="flex-1 px-5 py-3 hover:bg-neutral-50 transition-colors rounded-full cursor-text">
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold">Arrivée</div>
          <input
            data-testid="search-checkin"
            type="date"
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
            className="w-full bg-transparent text-sm font-semibold outline-none mt-0.5"
          />
        </label>
        <div className="w-px h-8 bg-black/10 self-center" />
        <label className="flex-1 px-5 py-3 hover:bg-neutral-50 transition-colors rounded-full cursor-text">
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold">Départ</div>
          <input
            data-testid="search-checkout"
            type="date"
            value={checkOut}
            onChange={(e) => setCheckOut(e.target.value)}
            className="w-full bg-transparent text-sm font-semibold outline-none mt-0.5"
          />
        </label>
        <div className="w-px h-8 bg-black/10 self-center" />
        <div className="relative flex-1" ref={popRef}>
          <button
            type="button"
            data-testid="search-guests-trigger"
            onClick={() => setOpen((o) => !o)}
            className="w-full text-left px-5 py-3 hover:bg-neutral-50 transition-colors rounded-full"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold">Voyageurs</div>
            <div className="text-sm font-semibold mt-0.5 truncate">{summary}</div>
          </button>
          {open && (
            <div
              data-testid="search-guests-pop"
              className="absolute right-0 sm:right-auto sm:left-1/2 sm:-translate-x-1/2 mt-2 w-80 max-w-[92vw] bg-white border border-black/15 shadow-2xl rounded-lg p-4 z-50"
            >
              <div className="divide-y divide-black/5">
                <Stepper label="Adultes" sub="13 ans et +" val={adults} set={setAdults} min={1} testid="adults" />
                <Stepper label="Enfants" sub="2 – 12 ans" val={children} set={setChildren} min={0} testid="children" />
                <Stepper label="Bébés" sub="moins de 2 ans" val={infants} set={setInfants} min={0} testid="infants" />
                <Stepper label="Animaux" sub="filtrer les logements pet-friendly" val={pets} set={setPets} min={0} testid="pets" />
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="mt-3 w-full text-xs uppercase tracking-[0.18em] font-bold underline"
              >
                Valider
              </button>
            </div>
          )}
        </div>
        <button
          data-testid="search-submit-btn"
          type="submit"
          className="shrink-0 bg-[#0A0A0A] hover:bg-[#1B2A49] text-white px-5 py-4 rounded-full flex items-center justify-center gap-2 transition-colors font-semibold"
        >
          <Search className="w-4 h-4" /> Chercher
        </button>
      </div>
    </form>
  );
}
